import useEmployeeCreateRouteConfig from "../../../../routes/configs/employees/EmployeeCreateRouteConfig";
import useEmployeeEditRouteConfig from "../../../../routes/configs/employees/EmployeeEditRouteConfig";
import DataTableConfig from "../../main/config/dataTable/DataTableConfig";
import useBasicColumnConfig from "../../main/config/column/default/BasicColumnConfig";
import useBooleanColumnConfig from "../../main/config/column/default/BooleanColumnConfig";
import useTimestampColumnConfig from "../../main/config/column/default/TimestampColumnConfig";
import useEditRowActionConfig from "../../main/config/rowAction/default/EditRowActionConfig";
import useDeleteRowActionConfig from "../../main/config/rowAction/default/DeleteRowActionConfig";
import useCreateHeaderActionConfig from "../../main/config/headerAction/default/CreateHeaderActionConfig";
import DataTable, { useDataTable } from "../../main/DataTable";
import useExportHeaderActionConfig from "../../main/config/headerAction/default/ExportHeaderActionConfig";
import useImportHeaderActionConfig from "../../main/config/headerAction/default/ImportHeaderActionConfig";
import useCompanyColumnConfig from "../../main/config/column/default/CompanyColumnConfig";
import RowActionConfig from "../../main/config/rowAction/RowActionConfig";
import { faSignIn, faSignOut } from "@fortawesome/free-solid-svg-icons";
import { useLanguageContext } from "../../../../context/LanguageContext";
import { useAuthContext } from "../../../../context/AuthContext";
import useEvaApi, { EvaApiCall } from "../../../../hooks/useEvaApi";

function EmployeesDataTable() {
  const callEvaApi = useEvaApi();
  const { translate } = useLanguageContext();
  const authContext = useAuthContext();

  //Create the datatable config
  const config = new DataTableConfig("employees");

  //Set the datatable fetch info
  config.setFetchRequest("employees");
  config.setFetchParams({
    with_employee_company_data: true,
    with_employee_location_data: true,
    with_employee_department_data: true,
    with_employee_language_data: true,
  });

  //Set the datatable order by
  config.setOrderBy("last_name");
  config.setOrderDirection("asc");

  //set the datatable columns
  config.setColumns([
    useCompanyColumnConfig(),
    useBasicColumnConfig("location.name"),
    useBasicColumnConfig("registration_code").setActive(false),
    useBasicColumnConfig("title").setActive(false),
    useBasicColumnConfig("first_name").setLockActive(true),
    useBasicColumnConfig("last_name").setLockActive(true),
    useBasicColumnConfig("email").setLockActive(true),
    useBasicColumnConfig("phone_number").setActive(false),
    useBasicColumnConfig("license_plate").setActive(false),
    useBasicColumnConfig("department.name"),
    useBasicColumnConfig("language.label").setActive(false),
    useBooleanColumnConfig("emergency_response_officer"),
    useBooleanColumnConfig("present").setDataStyle((row) => ({
      backgroundColor: row.present
        ? `rgba(0, 128, 0, 0.1)`
        : `rgba(255, 0, 0, 0.1)`,
    })),
    useBasicColumnConfig("signed_in_at")
      .setActive(false)
      .setSearchable(false)
      .setOrderable(false),
    useTimestampColumnConfig("created_at"),
    useTimestampColumnConfig("updated_at"),
  ]);

  //Set the datatable row actions
  config.setRowActions([
    new RowActionConfig()
      .setIcon(faSignIn)
      .setTitle(
        translate("eva.main.entities.states.sign_in", [
          { key: "entities", value: translate("eva.main.entities.employee") },
        ]),
      )
      .setHidden(
        (row) =>
          row.present || authContext.auth?.user?.company?.type !== "default",
      )
      .setDisabled(
        (row) =>
          authContext.auth?.location === null ||
          (authContext.auth?.user?.employee === row.id &&
            !authContext.hasPermission("employees.update.self")) ||
          (authContext.auth?.user?.employee !== row.id &&
            !authContext.hasPermission("employees.update")),
      )
      .setLoadingGroups((row) => `employee_sign_in_${row.id}`)
      .setCallback(onSignIn),

    new RowActionConfig()
      .setIcon(faSignOut)
      .setTitle(
        translate("eva.main.entities.states.sign_out", [
          { key: "entities", value: translate("eva.main.entities.employee") },
        ]),
      )
      .setHidden(
        (row) =>
          !row.present || authContext.auth?.user?.company?.type !== "default",
      )
      .setDisabled(
        (row) =>
          authContext.auth?.location === null ||
          (authContext.auth?.user?.employee === row.id &&
            !authContext.hasPermission("employees.update.self")) ||
          (authContext.auth?.user?.employee !== row.id &&
            !authContext.hasPermission("employees.update")),
      )
      .setLoadingGroups((row) => `employee_sign_out_${row.id}`)
      .setCallback(onSignOut),

    useEditRowActionConfig("employee", useEmployeeEditRouteConfig()),
    useDeleteRowActionConfig("employee"),
  ]);

  //Set the datatable header actions
  config.setHeaderActions([
    useCreateHeaderActionConfig("employee", useEmployeeCreateRouteConfig()),
    useExportHeaderActionConfig("employees"),
    useImportHeaderActionConfig("employees"),
  ]);

  //Set show saved filters
  config.setShowSavedFilters(true);
  
  //Create the datatable object
  const dataTable = useDataTable(config);

  /**
   * Sign in employee
   * @param {object} row
   */
  async function onSignIn(row) {
    await callEvaApi(
      new EvaApiCall(`employees/${row.id}/sign_in`)
        .setMethod("POST")
        .setLoadingGroup(`employee_sign_in_${row.id}`)
        .setAlertError(true)
        .setAlertSuccess(true)
        .setOnSuccess(() => {
          dataTable.fetch();
        }),
    );
  }

  /**
   * Sign out employee
   * @param {object} row
   */
  async function onSignOut(row) {
    await callEvaApi(
      new EvaApiCall(`employees/${row.id}/sign_out`)
        .setMethod("POST")
        .setLoadingGroup(`employee_sign_out_${row.id}`)
        .setAlertError(true)
        .setAlertSuccess(true)
        .setOnSuccess(() => {
          dataTable.fetch();
        }),
    );
  }

  return <DataTable controller={dataTable} />;
}

export default EmployeesDataTable;
